import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const WIPE_MS = 1500;

/* Reveal: word appears left → right */
const revealLR = keyframes`
  0%, 60%  { clip-path: inset(0 100% 0 0); }
  100%     { clip-path: inset(0 0 0 0); }
`;

/* Erase: word disappears right → left (shrinks from the right) */
const eraseRL = keyframes`
  0%        { clip-path: inset(0 0 0 0); }
  40%, 100% { clip-path: inset(0 100% 0 0); }
`;

const Wrapper = styled.span`
  display: inline-block;
  position: relative;
  width: 5ch;
  text-align: center;
  vertical-align: baseline;
`;

const Base = styled.span`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  display: inline-block;
  width: 100%;
  text-align: center;
`;

const Visible = styled(Base)`
  clip-path: inset(0 0 0 0);
`;

const Leaving = styled(Base)`
  animation: ${eraseRL} ${WIPE_MS}ms linear forwards;
`;

const Entering = styled(Base)`
  position: absolute;
  left: 0;
  top: 0;
  animation: ${revealLR} ${WIPE_MS}ms linear forwards;
`;

type Phase = 'hold-human' | 'wipe-to-ai' | 'hold-ai' | 'wipe-to-human';

const InterfacesDiagram: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('hold-human');
  const [tick, setTick] = useState(0);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const durations: Record<Phase, number> = {
      'hold-human': 1000,
      'wipe-to-ai': WIPE_MS,
      'hold-ai': 1000,
      'wipe-to-human': WIPE_MS,
    };
    const next: Record<Phase, Phase> = {
      'hold-human': 'wipe-to-ai',
      'wipe-to-ai': 'hold-ai',
      'hold-ai': 'wipe-to-human',
      'wipe-to-human': 'hold-human',
    };

    timeout.current = setTimeout(() => {
      if (phase === 'hold-human' || phase === 'hold-ai') {
        setTick((t) => t + 1);
      }
      setPhase(next[phase]);
    }, durations[phase]);

    return () => clearTimeout(timeout.current);
  }, [phase]);

  if (phase === 'hold-human') {
    return <Wrapper><Visible>Human</Visible></Wrapper>;
  }
  if (phase === 'wipe-to-ai') {
    return (
      <Wrapper>
        <Leaving key={`out-${tick}`}>Human</Leaving>
        <Entering key={`in-${tick}`}>AI</Entering>
      </Wrapper>
    );
  }
  if (phase === 'hold-ai') {
    return <Wrapper><Visible>AI</Visible></Wrapper>;
  }
  return (
    <Wrapper>
      <Leaving key={`out-${tick}`}>AI</Leaving>
      <Entering key={`in-${tick}`}>Human</Entering>
    </Wrapper>
  );
};

export default InterfacesDiagram;
